import { Flex, Form, Input, Select, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { descriptionTextAreaProps } from "../helpers/reportFormFields.ts"
import {
  folderFromSelection,
  folderSelection,
  type ReportSettingsValues
} from "../helpers/reportSettings.ts"

const { Text } = Typography

// Matches the model's CharField, so an over-long name fails in the field
// instead of as a 400.
const NAME_MAX_LENGTH = 255

interface ReportGeneralFieldsProps {
  value: ReportSettingsValues
  onChange: (patch: Partial<ReportSettingsValues>) => void
  folderOptions: string[]
  nameError: boolean
  /** Only a modal may take the cursor on open; a full page must not. */
  autoFocusName?: boolean
}

/**
 * The four metadata fields a report is authored with, shared by the create
 * wizard's first step and the settings modal so the two cannot drift on copy,
 * presentation or the folder rule.
 *
 * Renders bare `Form.Item`s: each host owns its own `<Form>` and whatever else
 * sits alongside them.
 */
export const ReportGeneralFields = ({
  value,
  onChange,
  folderOptions,
  nameError,
  autoFocusName = false
}: ReportGeneralFieldsProps) => {
  const { t } = useTranslation()

  return (
    <>
      <Form.Item
        required
        label={t("reports.wizard.general.name")}
        validateStatus={nameError ? "error" : undefined}
        help={nameError ? t("reports.wizard.general.nameRequired") : undefined}
      >
        <Input
          autoFocus={autoFocusName}
          value={value.name}
          maxLength={NAME_MAX_LENGTH}
          placeholder={t("reports.wizard.general.namePlaceholder")}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </Form.Item>

      <Form.Item label={t("reports.wizard.general.description")}>
        <Input.TextArea
          {...descriptionTextAreaProps}
          value={value.description}
          placeholder={t("reports.wizard.general.descriptionPlaceholder")}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </Form.Item>

      <Form.Item
        label={
          <Flex align="center" gap={4}>
            <span>{t("reports.wizard.general.folder")}</span>
            <Text type="secondary">{t("reports.wizard.general.optional")}</Text>
          </Flex>
        }
      >
        <Select
          allowClear
          mode="tags"
          aria-label={t("reports.wizard.general.folder")}
          value={folderSelection(value.folder)}
          placeholder={t("reports.wizard.general.folderPlaceholder")}
          options={folderOptions.map((folder) => ({
            label: folder,
            value: folder
          }))}
          onChange={(folders) =>
            onChange({ folder: folderFromSelection(folders) })
          }
        />
      </Form.Item>

      <Form.Item label={t("reports.wizard.general.tags")}>
        <Select
          mode="tags"
          aria-label={t("reports.wizard.general.tags")}
          value={value.tags}
          tokenSeparators={[",", " "]}
          placeholder={t("reports.wizard.general.tagsPlaceholder")}
          onChange={(tags) => onChange({ tags })}
        />
      </Form.Item>
    </>
  )
}
